import { WeeklyPlan, WeeklyAllocation } from '../types';

/**
 * Campos de estado de una asignación semanal que solo pueden cambiar por acción manual.
 * Al guardar, se conserva siempre el valor con el sello manual (statusUpdatedAt) más reciente,
 * evitando que copias desactualizadas (otros dispositivos, colas sin conexión) los reviertan.
 */
export const MANUAL_STATUS_FIELDS: (keyof WeeklyAllocation)[] = [
    'executed',
    'percentCumplimiento',
    'cncId',
    'comentario',
];

const allocKey = (a: WeeklyAllocation) => `${a.taskId}__${a.unitId}`;

const ts = (a?: WeeklyAllocation) => {
    const t = a?.statusUpdatedAt ? Date.parse(a.statusUpdatedAt) : NaN;
    return isNaN(t) ? 0 : t;
};

/**
 * Combina el estado local (que se va a guardar) con el remoto (servidor).
 * La estructura (planes y asignaciones) se toma del local; los campos de estado
 * de cada asignación se toman de la versión con el cambio manual más reciente.
 */
export function mergeWeeklyPlans(local: WeeklyPlan[], remote: WeeklyPlan[]): WeeklyPlan[] {
    if (!Array.isArray(local) || !Array.isArray(remote) || remote.length === 0) return local;

    const remotePlans = new Map<number, WeeklyPlan>();
    remote.forEach(p => p && remotePlans.set(p.id, p));

    return local.map(plan => {
        const rPlan = remotePlans.get(plan.id);
        if (!rPlan || !Array.isArray(rPlan.allocations)) return plan;

        const rAllocs = new Map<string, WeeklyAllocation>();
        rPlan.allocations.forEach(a => a && rAllocs.set(allocKey(a), a));

        let changed = false;
        const allocations = (plan.allocations || []).map(a => {
            const r = rAllocs.get(allocKey(a));
            if (!r) return a;
            if (ts(r) > ts(a)) {
                changed = true;
                const merged: WeeklyAllocation = { ...a };
                MANUAL_STATUS_FIELDS.forEach(f => {
                    (merged as any)[f] = (r as any)[f];
                });
                merged.statusUpdatedAt = r.statusUpdatedAt;
                merged.statusUpdatedBy = r.statusUpdatedBy;
                return merged;
            }
            return a;
        });

        return changed ? { ...plan, allocations } : plan;
    });
}
